import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, timeAgo } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Bell, ChatCircle, CheckCircle, At, Medal, Briefcase, ArrowRight, ShieldWarning, Lightning } from "@phosphor-icons/react";

const ICONS = {
    answer: ChatCircle,
    comment: ChatCircle,
    accept: CheckCircle,
    mention: At,
    badge: Medal,
    application_status_changed: Briefcase,
    mod_action: ShieldWarning,
    ai_engine: Lightning,
};

const TINTS = {
    answer: "bg-cyan-500/10 text-cyan-400",
    comment: "bg-cyan-500/10 text-cyan-400",
    accept: "bg-emerald-500/10 text-emerald-400",
    mod_action: "bg-amber-500/10 text-amber-400",
    ai_engine: "bg-cyan-500/10 text-cyan-400",
    mention: "bg-violet-500/10 text-violet-400",
    badge: "bg-amber-500/10 text-amber-400",
    application_status_changed: "bg-emerald-500/10 text-emerald-400",
};

export default function NotificationBell() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [count, setCount] = useState(0);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const ref = useRef(null);

    const pollCount = async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications/unread-count");
            setCount(data.count || 0);
            if ((data.count || 0) > 0) checkQuestToasts();
        } catch { /* noop */ }
    };

    const checkQuestToasts = async () => {
        try {
            const { data } = await api.get("/notifications?limit=5");
            const seen = new Set(JSON.parse(localStorage.getItem("toasted_notifs") || "[]"));
            let changed = false;
            (data.items || data || []).forEach((n) => {
                if (n.read || seen.has(n.notification_id)) return;
                if (n.kind === "quest_complete") {
                    toast.success(n.message, { icon: "🏆", duration: 6000 });
                    seen.add(n.notification_id); changed = true;
                } else if (n.kind === "streak_nudge") {
                    toast(n.message, { icon: "🔥", duration: 6000 });
                    seen.add(n.notification_id); changed = true;
                }
            });
            if (changed) localStorage.setItem("toasted_notifs", JSON.stringify([...seen].slice(-50)));
        } catch { /* noop */ }
    };

    useEffect(() => {
        if (!user) { setCount(0); setItems([]); return; }
        pollCount();
        const id = setInterval(pollCount, 30000);
        return () => clearInterval(id);
    }, [user]);

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("keydown", onKey);
        return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
    }, []);

    const loadList = async () => {
        if (!user) return;
        setLoading(true);
        try {
            const { data } = await api.get("/notifications?limit=10");
            setItems(data);
        } catch { /* noop */ }
        setLoading(false);
    };

    const toggle = () => {
        setOpen((v) => {
            if (!v) loadList();
            return !v;
        });
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/mark-read", { ids: null });
            setCount(0);
            setItems((arr) => arr.map((n) => ({ ...n, read: true })));
        } catch { /* noop */ }
    };

    const onItemClick = async (n) => {
        if (!n.read) {
            try { await api.post("/notifications/mark-read", { ids: [n.notification_id] }); }
            catch { /* noop */ }
            setCount((c) => Math.max(0, c - 1));
        }
        setOpen(false);
    };

    if (!user) return null;

    return (
        <div className="relative" ref={ref}>
            <button onClick={toggle} aria-haspopup="dialog" aria-expanded={open} className="relative flex items-center justify-center w-11 h-11 sm:w-9 sm:h-9 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-white/5 transition-colors" data-testid="notif-bell" aria-label="Notifications">
                <Bell size={18} weight={count > 0 ? "fill" : "duotone"} className={count > 0 ? "text-cyan-400" : ""} />
                {count > 0 && (
                    <span className="absolute top-0 right-0 min-w-[16px] h-4 bg-rose-500 text-[9px] font-bold text-white rounded-full flex items-center justify-center px-1" data-testid="notif-badge">
                        {count > 9 ? "9+" : count}
                    </span>
                )}
            </button>
            {open && (
                <div className="hc-cmdk-enter fixed sm:absolute right-2 sm:right-0 top-14 sm:top-12 z-50 w-[calc(100vw-1rem)] sm:w-96 max-w-md bg-[#0d1424] rounded-sm ring-1 ring-white/10 shadow-2xl shadow-black/60 overflow-hidden flex flex-col" data-testid="notif-dropdown">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
                        <div className="text-sm font-medium text-slate-100">
                            Notifications
                            {count > 0 && <span className="ml-2 text-[11px] font-mono text-cyan-400">{count} new</span>}
                        </div>
                        {count > 0 && (
                            <button onClick={markAllRead} className="text-[11px] text-slate-400 hover:text-cyan-300 transition-colors" data-testid="mark-all-read">
                                Mark all read
                            </button>
                        )}
                    </div>
                    <div className="max-h-[400px] overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center gap-2 px-4 py-6 text-xs font-mono text-slate-500">
                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> loading…
                            </div>
                        ) : items.length === 0 ? (
                            <div className="flex flex-col items-center gap-2 py-10 px-4 text-center">
                                <div className="w-11 h-11 rounded-sm bg-white/5 ring-1 ring-white/10 flex items-center justify-center">
                                    <Bell size={19} weight="duotone" className="text-slate-500" />
                                </div>
                                <div className="text-sm text-slate-200">You're all caught up</div>
                                <div className="text-[11px] text-slate-500">New activity will show up here.</div>
                            </div>
                        ) : (
                            items.map((n) => {
                                const Icon = ICONS[n.kind] || Bell;
                                const tint = TINTS[n.kind] || "bg-cyan-500/10 text-cyan-400";
                                return (
                                    <Link
                                        key={n.notification_id}
                                        to={n.link || "/"}
                                        onClick={() => onItemClick(n)}
                                        className={`group flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${!n.read ? "bg-cyan-500/[0.05]" : ""}`}
                                        data-testid={`notif-${n.notification_id}`}
                                    >
                                        <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${tint}`}>
                                            <Icon size={15} weight="duotone" />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-slate-300 leading-snug">
                                                <span className="text-slate-100 font-semibold">@{n.actor_username}</span>
                                                {" "}
                                                <span>{n.message || n.kind}</span>
                                            </div>
                                            {n.target_title && (
                                                <div className="text-[12px] text-slate-400 mt-0.5 line-clamp-1">"{n.target_title}"</div>
                                            )}
                                            <div className="text-[11px] text-slate-500 mt-1">{timeAgo(n.created_at)}</div>
                                        </div>
                                        {!n.read && <span className="w-2 h-2 bg-cyan-400 rounded-full mt-1.5 shrink-0" />}
                                    </Link>
                                );
                            })
                        )}
                    </div>
                    <Link
                        to="/inbox"
                        onClick={() => setOpen(false)}
                        className="flex items-center justify-center gap-2 px-4 py-3 border-t border-white/8 text-[11px] uppercase tracking-widest text-cyan-300 hover:bg-cyan-500/5 transition-colors"
                        data-testid="view-all-notifs"
                    >
                        View all <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            )}
        </div>
    );
}
